// @flow

import * as React from 'react';
import { bind } from '@ta/common.api';
import logging from '@ta/common.logging';
import { HideOnReadonly } from '@ta/failover.readonly';
import Radio from '@ta/common.radio';

type OwnProps = {|
  children: ({|
    ref: {| current: HTMLDivElement | null |},
    onClick: (SyntheticMouseEvent<*>) => mixed,
    hasUnreadConvos: boolean,
  |}) => React.Node,
|};

type Props = {|
  ...OwnProps,

  /** Show a jewel on the icon, such as indicating a notification. */
  hasUnreadConvos: boolean,

  /** Whether to show dropdown or just reload page to /Inbox */
  showDropdown: boolean,

  /** Whether or not we're on tablet */
  isTablet: boolean,
|};

const OPEN_REQUEST_RECEIVED_CHECK_TIMEOUT = 100; // Arbitrarily small-ish value. Should definitely receive the response back within this time..

/**
 * Provides a callback to open the inbox overlay. This can be used to provide an alternate UI
 * for {@link ./InboxTrigger}.
 */
class InboxTrigger extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.containerEl = React.createRef();
    // Used to ensure the dropdown placement [global_nav_action_inbox:empty] received the message, otherwise we should redirect to /Inbox as a last resort.
    this.lastOpenRequestReceived = false;
    this.lastOpenRequestTimer = null;
  }

  clickHandler = () => {
    const { current } = this.containerEl;
    if (this.props.showDropdown) {
      // eslint-disable-next-line no-restricted-globals
      window.location = '/Inbox';
    } else if (current) {
      // Eye-balled to match 2017 header.
      let bottomLeftOffset;
      if (this.props.isTablet) {
        bottomLeftOffset = [-5, 21];
      } else {
        bottomLeftOffset = [3, 11];
      }

      this.lastOpenRequestReceived = false;
      Radio('global-nav-inbox').emit('open', {
        el: this.containerEl.current,
        bottomLeftOffset,
        receivedCallback: () => {
          this.lastOpenRequestReceived = true;
        },
      });
      setTimeout(this.checkOpenRequestReceived, OPEN_REQUEST_RECEIVED_CHECK_TIMEOUT);
    }
  };

  /**
   * If we didn't hear back from the inbox placement via Radio, redirect to the Inbox.
   */
  checkOpenRequestReceived = () => {
    if (!this.lastOpenRequestReceived) {
      logging.error(
        'Inbox placement global_nav_action_inbox:empty did not respond back about receiving the inbox dropdown open request via Radio. Redirecting to /Inbox as a fallback.',
      );
      // eslint-disable-next-line no-restricted-globals
      window.location = '/Inbox';
    }
  };

  containerEl: {| current: HTMLDivElement | null |};
  lastOpenRequestReceived: boolean;
  lastOpenRequestTimer: number | null;

  render = () => (
    <HideOnReadonly>
      {this.props.children({
        ref: this.containerEl,
        onClick: this.clickHandler,
        hasUnreadConvos: this.props.hasUnreadConvos,
      })}
    </HideOnReadonly>
  );
}

/** Function to get the  parameters for the GlobalNav api call */
const getInboxParams = state => {
  const isMember = state.auth.isMember ? 'true' : 'false';

  const paramMap = {
    isMember, // Not used by endpoint but used to cachebust if user logs in.
  };

  return new URLSearchParams(paramMap);
};

export default bind<Props, {||}, _, _, _, _>((state, stage) => {
  const inboxApiCall = stage(`/data/1.0/inbox/navAction?${getInboxParams(state).toString()}`);

  return newState => {
    const response = inboxApiCall.get().data || {};

    return {
      hasUnreadConvos: response.hasUnreadConvos || false,
      showDropdown: !newState.route.page === 'Inbox',
      isTablet: newState.meta.device.viewportCategory === 'TABLET',
    };
  };
})(InboxTrigger);
